import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  Copy,
  Eraser,
  Save,
  Sun,
  Trash2,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  schedulesApi,
  type WeekplanDayMasks,
  type WeekplanDetail,
} from "../api/schedulesApi";
import {
  dayKeys,
  dayLabels,
  maskToSlots,
  slotToTime,
  slotsToMask,
  SLOTS_PER_DAY,
  type DayKey,
} from "../lib/weekplan";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: WeekplanDetail }
  | { status: "error"; message: string };

type SlotGrid = Record<DayKey, boolean[]>;

function toGrid(masks: WeekplanDayMasks): SlotGrid {
  return {
    mon: maskToSlots(masks.mon),
    tue: maskToSlots(masks.tue),
    wed: maskToSlots(masks.wed),
    thu: maskToSlots(masks.thu),
    fri: maskToSlots(masks.fri),
    sat: maskToSlots(masks.sat),
    sun: maskToSlots(masks.sun),
  };
}

function gridToMasks(grid: SlotGrid): WeekplanDayMasks {
  return {
    mon: slotsToMask(grid.mon),
    tue: slotsToMask(grid.tue),
    wed: slotsToMask(grid.wed),
    thu: slotsToMask(grid.thu),
    fri: slotsToMask(grid.fri),
    sat: slotsToMask(grid.sat),
    sun: slotsToMask(grid.sun),
  };
}

function emptyDay(): boolean[] {
  return new Array<boolean>(SLOTS_PER_DAY).fill(false);
}

function cloneGrid(grid: SlotGrid): SlotGrid {
  return {
    mon: grid.mon.slice(),
    tue: grid.tue.slice(),
    wed: grid.wed.slice(),
    thu: grid.thu.slice(),
    fri: grid.fri.slice(),
    sat: grid.sat.slice(),
    sun: grid.sun.slice(),
  };
}

/**
 * Edit surface for a single weekplan. PageHeader (Back + Save), a
 * details tile (name), a large "Open hours" tile with the interactive
 * click-drag grid, and a danger-zone delete tile. The grid is the main
 * attraction: Monday-first, kwartier per rij, 7 kolommen, auto-drag
 * mode (first cell determines whether the drag paints or erases).
 */
export function WeekplanDetailPage() {
  const { weekplanId: param } = useParams<{ weekplanId: string }>();
  const weekplanId = Number(param);
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [name, setName] = useState("");
  const [grid, setGrid] = useState<SlotGrid | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(weekplanId) || weekplanId <= 0) {
      setState({ status: "error", message: "Invalid weekplan id." });
      return;
    }
    try {
      const detail = await schedulesApi.getWeekplan(weekplanId);
      setState({ status: "ready", detail });
      setName(detail.name ?? "");
      setGrid(toGrid(detail.masks));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load weekplan";
      setState({ status: "error", message });
    }
  }, [weekplanId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isDirty = useMemo(() => {
    if (state.status !== "ready" || !grid) return false;
    if (name.trim() !== (state.detail.name ?? "").trim()) return true;
    const next = gridToMasks(grid);
    const cur = state.detail.masks;
    return (
      next.mon !== cur.mon ||
      next.tue !== cur.tue ||
      next.wed !== cur.wed ||
      next.thu !== cur.thu ||
      next.fri !== cur.fri ||
      next.sat !== cur.sat ||
      next.sun !== cur.sun
    );
  }, [state, grid, name]);

  const onSave = useCallback(async () => {
    if (state.status !== "ready" || !grid) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setSaveError("Name is required.");
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      const next = await schedulesApi.updateWeekplan(weekplanId, {
        name: trimmed,
        masks: gridToMasks(grid),
      });
      setState({ status: "ready", detail: next });
      setName(next.name ?? "");
      setGrid(toGrid(next.masks));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }, [state, grid, name, weekplanId]);

  const performDeleteWeekplan = useCallback(async () => {
    if (state.status !== "ready") return;
    setDeleteConfirmOpen(false);
    setBusy(true);
    setDeleteError(null);
    try {
      await schedulesApi.deleteWeekplan(weekplanId);
      navigate("/schedules");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setBusy(false);
    }
  }, [state, weekplanId, navigate]);

  const onDelete = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  if (state.status === "loading") {
    return (
      <>
        <PageHeader title="Weekplan" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading weekplan…
          </div>
        </div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Weekplan" />
        <div className="dashboard">
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Could not load this weekplan.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
            <div style={{ marginTop: 12 }}>
              <Link to="/schedules" className="btn">
                <ArrowLeft size={16} /> Back to schedules
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!grid) return null;

  return (
    <>
      <PageHeader
        title={state.detail.name || "Weekplan"}
        actions={
          <div style={{ display: "inline-flex", gap: 8 }}>
            <Link to="/schedules" className="btn">
              <ArrowLeft size={16} /> Back
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={busy || !isDirty}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Save size={16} />
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <section className="tile" aria-labelledby="wp-details">
          <header className="tile-head">
            <h3 id="wp-details">Details</h3>
          </header>
          <div style={{ display: "grid", gap: 12, maxWidth: 640 }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "var(--portal-muted)",
                  fontWeight: 600,
                }}
              >
                Name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                maxLength={50}
                className="portal-input-inline"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--portal-border)",
                  borderRadius: 6,
                  fontSize: "0.95rem",
                  fontFamily: "inherit",
                  background: "#ffffff",
                }}
              />
            </label>
            {saveError && (
              <div style={{ color: "#c0392b", fontSize: "0.9rem" }}>
                {saveError}
              </div>
            )}
          </div>
        </section>

        <section className="tile" aria-labelledby="wp-grid">
          <header
            className="tile-head"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h3 id="wp-grid">Open hours</h3>
            <GridHelpers
              disabled={busy}
              onPresetOffice={() =>
                setGrid((g) => (g ? applyOfficePreset(g) : g))
              }
              onClearAll={() =>
                setGrid((g) => (g ? clearAll(g) : g))
              }
            />
          </header>
          <p
            style={{
              margin: "0 0 12px 0",
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
            }}
          >
            Click and drag to paint the hours this plan should be open.
            The first cell you click decides whether you're opening or
            closing the dragged range. Each cell is 15&nbsp;minutes.
          </p>
          <WeekGrid
            grid={grid}
            onChange={setGrid}
            disabled={busy}
          />
        </section>

        <DangerZone
          name={state.detail.name}
          busy={busy}
          error={deleteError}
          onRequestDelete={onDelete}
        />
      </div>

      {state.status === "ready" && (
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!busy) setDeleteConfirmOpen(false);
          }}
          title="Delete weekplan?"
          confirmLabel="Delete"
          tone="danger"
          busy={busy}
          onConfirm={() => void performDeleteWeekplan()}
        >
          Delete weekplan &quot;{state.detail.name}&quot;? This cannot be
          undone.
        </ConfirmModal>
      )}
    </>
  );
}

function GridHelpers({
  disabled,
  onPresetOffice,
  onClearAll,
}: {
  disabled: boolean;
  onPresetOffice: () => void;
  onClearAll: () => void;
}) {
  return (
    <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn"
        onClick={onPresetOffice}
        disabled={disabled}
        title="Set Monday–Friday 09:00–17:00"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          fontSize: "0.85rem",
        }}
      >
        <Sun size={14} /> Office hours (Mon–Fri 9–17)
      </button>
      <button
        type="button"
        className="btn"
        onClick={onClearAll}
        disabled={disabled}
        title="Close the plan for every day"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          fontSize: "0.85rem",
        }}
      >
        <Eraser size={14} /> Clear all
      </button>
    </div>
  );
}

/**
 * The main click-drag grid. 7 columns (Mon..Sun) × 96 rows (15-minute
 * slots). Hour lines are emphasised with a thicker top border so the
 * eye can still see "that's 09:00" without an explicit label on every
 * row. Time labels only appear on the hour to keep the grid compact.
 *
 * Interaction model:
 *   - mouse down on a cell: remember whether it was open or closed.
 *     The drag "paints" the opposite value everywhere it touches.
 *   - mouse enter on another cell during drag: apply the same value.
 *   - mouse up / leave the window: end the drag.
 *   - global mouseup listener handles the "user released outside the
 *     grid" case so we never get stuck in drag mode.
 */
function WeekGrid({
  grid,
  onChange,
  disabled,
}: {
  grid: SlotGrid;
  onChange: (next: SlotGrid) => void;
  disabled: boolean;
}) {
  // Paint mode is decided on mousedown: whatever value the starting
  // cell had, we paint the opposite. Null = not currently dragging.
  const paintRef = useRef<boolean | null>(null);
  const [, forceRender] = useState(0); // nudge render when drag ends

  useEffect(() => {
    const end = () => {
      if (paintRef.current !== null) {
        paintRef.current = null;
        forceRender((n) => n + 1);
      }
    };
    window.addEventListener("mouseup", end);
    window.addEventListener("blur", end);
    return () => {
      window.removeEventListener("mouseup", end);
      window.removeEventListener("blur", end);
    };
  }, []);

  const paintCell = (day: DayKey, slot: number, value: boolean) => {
    if (grid[day][slot] === value) return;
    const next = cloneGrid(grid);
    next[day][slot] = value;
    onChange(next);
  };

  const onCellDown = (day: DayKey, slot: number) => (e: MouseEvent) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    const current = grid[day][slot];
    paintRef.current = !current;
    paintCell(day, slot, !current);
  };

  const onCellEnter = (day: DayKey, slot: number) => {
    if (disabled || paintRef.current === null) return;
    paintCell(day, slot, paintRef.current);
  };

  const copyDay = (source: DayKey, targets: DayKey[]) => {
    const next = cloneGrid(grid);
    for (const t of targets) {
      if (t === source) continue;
      next[t] = grid[source].slice();
    }
    onChange(next);
  };

  const clearDay = (day: DayKey) => {
    const next = cloneGrid(grid);
    next[day] = emptyDay();
    onChange(next);
  };

  // userSelect must stay "none" during a drag so the browser doesn't
  // accidentally select the labels while the user paints a range.
  const dragging = paintRef.current !== null;

  // Render BOTH layouts and let CSS show one based on viewport width —
  // the horizontal layout (days on rows, hours on columns) is compact
  // and at-a-glance readable on desktop, while the vertical layout
  // (hours on rows, days on columns) behaves better on a phone where
  // a 96-column grid would collapse into illegible slivers. Both
  // variants share the same drag handlers so clicks keep working no
  // matter which one is visible.

  return (
    <>
      <div
        className="weekplan-grid weekplan-grid-horizontal"
        style={{
          userSelect: dragging ? "none" : "auto",
          border: "1px solid var(--portal-border)",
          borderRadius: 8,
          background: "#ffffff",
          width: "100%",
          // No scrollbar in any viewport: cells shrink to whatever
          // horizontal space the tile offers via `minmax(0, 1fr)`.
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "var(--weekplan-day-col, 56px) repeat(96, minmax(0, 1fr))",
            gridTemplateRows:
              "var(--weekplan-header-height, 24px) repeat(7, var(--weekplan-day-height, 28px))",
          }}
        >
          {/* Top-left corner: empty placeholder above the day-label column. */}
          <div
            style={{
              borderRight: "1px solid var(--portal-border)",
              borderBottom: "1px solid var(--portal-border)",
              background: "var(--portal-surface-alt)",
            }}
          />
          {Array.from({ length: 24 }, (_, hour) => (
            <HourLabel key={`hour-${hour}`} hour={hour} />
          ))}
          {dayKeys.map((day) => (
            <DayRow
              key={day}
              day={day}
              grid={grid}
              disabled={disabled}
              onDown={onCellDown}
              onEnter={onCellEnter}
              onCopyTo={(targets) => copyDay(day, targets)}
              onClearDay={() => clearDay(day)}
            />
          ))}
        </div>
      </div>

      <div
        className="weekplan-grid weekplan-grid-vertical"
        style={{
          userSelect: dragging ? "none" : "auto",
          border: "1px solid var(--portal-border)",
          borderRadius: 8,
          background: "#ffffff",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            // Time-column on the left + 7 day columns, each one flex
            // (minmax(0, 1fr) lets them shrink to fit the tile width on
            // very narrow phones). The total grid height is 24 hours ×
            // 40px = 960px (32/28px on smaller screens, see CSS below)
            // which is still fully visible without its own scrollbar.
            gridTemplateColumns:
              "var(--weekplan-time-col, 56px) repeat(7, minmax(0, 1fr))",
            gridAutoRows: "var(--weekplan-hour-height, 40px)",
          }}
        >
          {/* Header row: empty corner + day label (with action menu). */}
          <div />
          {dayKeys.map((d) => (
            <DayHeader
              key={d}
              day={d}
              disabled={disabled}
              onCopyTo={(targets) => copyDay(d, targets)}
              onClearDay={() => clearDay(d)}
            />
          ))}

          {/* 24 "hour blocks" stacked vertically. */}
          {Array.from({ length: 24 }, (_, hour) => (
            <HourRow
              key={hour}
              hour={hour}
              grid={grid}
              disabled={disabled}
              onDown={onCellDown}
              onEnter={onCellEnter}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function HourRow({
  hour,
  grid,
  disabled,
  onDown,
  onEnter,
}: {
  hour: number;
  grid: SlotGrid;
  disabled: boolean;
  onDown: (day: DayKey, slot: number) => (e: MouseEvent) => void;
  onEnter: (day: DayKey, slot: number) => void;
}) {
  const firstSlot = hour * 4;
  return (
    <>
      <div
        aria-hidden="true"
        className="weekplan-time-label"
        style={{
          fontSize: "0.72rem",
          color: "var(--portal-muted)",
          textAlign: "right",
          paddingRight: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "100%",
          // Hour numbers sit on the border line, calendar-app style.
          transform: "translateY(-50%)",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {hour.toString().padStart(2, "0")}:00
      </div>
      {dayKeys.map((day) => {
        const isMonday = day === "mon";
        const isSunday = day === "sun";
        return (
          <div
            key={`${day}-${hour}`}
            style={{
              display: "grid",
              gridTemplateRows: "repeat(4, 1fr)",
              borderLeft: isMonday
                ? "1px solid var(--portal-border)"
                : "1px solid var(--portal-surface-alt)",
              borderRight: isSunday
                ? "1px solid var(--portal-border)"
                : "none",
              borderTop: "1px solid var(--portal-border)",
              borderBottom:
                hour === 23 ? "1px solid var(--portal-border)" : "none",
            }}
          >
            {Array.from({ length: 4 }, (_, q) => {
              const slot = firstSlot + q;
              const open = grid[day][slot];
              return (
                <button
                  key={`${day}-${slot}`}
                  type="button"
                  disabled={disabled}
                  onMouseDown={onDown(day, slot)}
                  onMouseEnter={() => onEnter(day, slot)}
                  aria-label={`${dayLabels[day]} ${slotToTime(slot)} ${
                    open ? "open" : "closed"
                  }`}
                  aria-pressed={open}
                  title={`${dayLabels[day]} ${slotToTime(slot)}–${slotToTime(
                    slot + 1,
                  )}`}
                  style={{
                    all: "unset",
                    cursor: disabled ? "not-allowed" : "pointer",
                    background: open
                      ? "var(--portal-primary)"
                      : "var(--portal-surface)",
                    borderTop:
                      q === 0
                        ? "none"
                        : "1px solid var(--portal-surface-alt)",
                    transition: "background 80ms linear",
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function DayHeader({
  day,
  disabled,
  onCopyTo,
  onClearDay,
}: {
  day: DayKey;
  disabled: boolean;
  onCopyTo: (targets: DayKey[]) => void;
  onClearDay: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div
      className="weekplan-day-head"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: "6px 4px",
        fontSize: "0.85rem",
        fontWeight: 600,
        color: "var(--portal-text-soft)",
        borderBottom: "1px solid var(--portal-border)",
        background: "var(--portal-surface-alt)",
        position: "relative",
      }}
    >
      <span>{dayLabels[day]}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${dayLabels[day]} actions`}
        style={{
          padding: "2px 2px",
          background: "transparent",
          border: "none",
          color: "var(--portal-muted)",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "inline-flex",
        }}
      >
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            // Anchor to the right edge of Sunday (last day column) so
            // the menu never clips off the right of the tile on the
            // narrowest screens where this vertical layout is shown.
            right: 4,
            background: "#ffffff",
            border: "1px solid var(--portal-border)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            padding: 4,
            minWidth: 200,
            zIndex: 10,
            fontWeight: 400,
          }}
        >
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to weekdays (Mon–Fri)"
            onClick={() => {
              onCopyTo(["mon", "tue", "wed", "thu", "fri"]);
              setOpen(false);
            }}
          />
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to weekend (Sat–Sun)"
            onClick={() => {
              onCopyTo(["sat", "sun"]);
              setOpen(false);
            }}
          />
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to all other days"
            onClick={() => {
              onCopyTo(dayKeys);
              setOpen(false);
            }}
          />
          <div
            style={{
              height: 1,
              background: "var(--portal-border)",
              margin: "4px 0",
            }}
          />
          <MenuItem
            icon={<Eraser size={14} />}
            label="Clear this day"
            onClick={() => {
              onClearDay();
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function HourLabel({ hour }: { hour: number }) {
  const label = `${hour.toString().padStart(2, "0")}:00`;
  // On mobile we fade out anything that's not a 3-hour boundary, see
  // weekplan-grid CSS rules. Keeping the label in the DOM means the
  // column widths stay consistent — only the text disappears.
  const isTriHour = hour % 3 === 0;
  return (
    <div
      aria-hidden="true"
      className={`weekplan-hour-label${isTriHour ? " is-tri-hour" : ""}`}
      style={{
        gridColumn: "span 4",
        borderLeft: "1px solid var(--portal-border)",
        borderBottom: "1px solid var(--portal-border)",
        background: "var(--portal-surface-alt)",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingLeft: 4,
        fontSize: "0.7rem",
        color: "var(--portal-muted)",
        fontWeight: 600,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
  );
}

function DayRow({
  day,
  grid,
  disabled,
  onDown,
  onEnter,
  onCopyTo,
  onClearDay,
}: {
  day: DayKey;
  grid: SlotGrid;
  disabled: boolean;
  onDown: (day: DayKey, slot: number) => (e: MouseEvent) => void;
  onEnter: (day: DayKey, slot: number) => void;
  onCopyTo: (targets: DayKey[]) => void;
  onClearDay: () => void;
}) {
  const isLast = day === "sun";
  return (
    <>
      <DayLabelCell
        day={day}
        disabled={disabled}
        isLast={isLast}
        onCopyTo={onCopyTo}
        onClearDay={onClearDay}
      />
      {Array.from({ length: SLOTS_PER_DAY }, (_, slot) => {
        const open = grid[day][slot];
        // Every 4th slot starts a new hour; every 16th slot starts a
        // "quarter of the day" (for a secondary rhythm line). We scale
        // the left-border colour accordingly so the grid still reads
        // like a clock even with no labels in the body.
        const isHourStart = slot % 4 === 0;
        const isLastSlot = slot === SLOTS_PER_DAY - 1;
        return (
          <button
            key={`${day}-${slot}`}
            type="button"
            disabled={disabled}
            onMouseDown={onDown(day, slot)}
            onMouseEnter={() => onEnter(day, slot)}
            aria-label={`${dayLabels[day]} ${slotToTime(slot)} ${
              open ? "open" : "closed"
            }`}
            aria-pressed={open}
            title={`${dayLabels[day]} ${slotToTime(slot)}–${slotToTime(slot + 1)}`}
            style={{
              all: "unset",
              cursor: disabled ? "not-allowed" : "pointer",
              background: open
                ? "var(--portal-primary)"
                : "var(--portal-surface)",
              // Hour seams are --portal-border (subtle but readable);
              // quarter seams are --portal-surface-alt (almost
              // invisible). Consistent 1px width everywhere keeps the
              // rhythm clean across a very narrow mobile grid.
              borderLeft: isHourStart
                ? "1px solid var(--portal-border)"
                : "1px solid var(--portal-surface-alt)",
              borderRight: isLastSlot
                ? "1px solid var(--portal-border)"
                : "none",
              borderBottom: isLast
                ? "1px solid var(--portal-border)"
                : "1px solid var(--portal-surface-alt)",
              transition: "background 80ms linear",
            }}
          />
        );
      })}
    </>
  );
}

function DayLabelCell({
  day,
  disabled,
  isLast,
  onCopyTo,
  onClearDay,
}: {
  day: DayKey;
  disabled: boolean;
  isLast: boolean;
  onCopyTo: (targets: DayKey[]) => void;
  onClearDay: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div
      className="weekplan-day-cell"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 6px",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "var(--portal-text-soft)",
        borderRight: "1px solid var(--portal-border)",
        borderBottom: isLast
          ? "1px solid var(--portal-border)"
          : "1px solid var(--portal-surface-alt)",
        background: "var(--portal-surface-alt)",
        position: "relative",
        overflow: "visible",
      }}
    >
      <span
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {dayLabels[day]}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${dayLabels[day]} actions`}
        style={{
          // Not using .btn here so the caret doesn't get extra padding
          // from the global button styles — the day label column is
          // deliberately narrow.
          padding: "2px 2px",
          background: "transparent",
          border: "none",
          color: "var(--portal-muted)",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "inline-flex",
        }}
      >
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            // Day rows pivoted horizontally — the menu now has to open
            // to the RIGHT of the day-label cell, next to the slot grid,
            // so it doesn't block the caret.
            top: 0,
            left: "calc(100% + 4px)",
            background: "#ffffff",
            border: "1px solid var(--portal-border)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            padding: 4,
            minWidth: 220,
            zIndex: 10,
            fontWeight: 400,
          }}
        >
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to weekdays (Mon–Fri)"
            onClick={() => {
              onCopyTo(["mon", "tue", "wed", "thu", "fri"]);
              setOpen(false);
            }}
          />
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to weekend (Sat–Sun)"
            onClick={() => {
              onCopyTo(["sat", "sun"]);
              setOpen(false);
            }}
          />
          <MenuItem
            icon={<Copy size={14} />}
            label="Copy to all other days"
            onClick={() => {
              onCopyTo(dayKeys);
              setOpen(false);
            }}
          />
          <div
            style={{
              height: 1,
              background: "var(--portal-border)",
              margin: "4px 0",
            }}
          />
          <MenuItem
            icon={<Eraser size={14} />}
            label="Clear this day"
            onClick={() => {
              onClearDay();
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        padding: "6px 10px",
        fontFamily: "inherit",
        fontSize: "0.9rem",
        cursor: "pointer",
        borderRadius: 6,
        color: "inherit",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background =
          "var(--portal-surface-alt)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = "transparent";
      }}
    >
      <span style={{ color: "var(--portal-muted)" }}>{icon}</span>
      {label}
    </button>
  );
}

function DangerZone({
  name,
  busy,
  error,
  onRequestDelete,
}: {
  name: string;
  busy: boolean;
  error: string | null;
  onRequestDelete: () => void;
}) {
  return (
    <section className="tile" aria-labelledby="wp-danger">
      <header className="tile-head">
        <h3 id="wp-danger">Delete</h3>
      </header>
      <p style={{ color: "var(--portal-muted)", margin: "0 0 12px 0" }}>
        Deleting a weekplan is permanent. Flow-builder nodes that still
        reference this weekplan will fall back to "always open" until you
        pick a replacement.
      </p>
      {error && (
        <div
          style={{
            marginBottom: 12,
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn"
        onClick={onRequestDelete}
        disabled={busy}
        title={`Delete weekplan "${name}"`}
        style={{
          background: "#ffffff",
          color: busy ? "var(--portal-muted)" : "#c0392b",
          borderColor: busy ? "var(--portal-border)" : "#e74c3c",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Trash2 size={16} />
        Delete weekplan
      </button>
    </section>
  );
}

// --- helpers --------------------------------------------------------

function applyOfficePreset(grid: SlotGrid): SlotGrid {
  // 09:00 -> slot 36, 17:00 -> slot 68 (each slot is 15 minutes).
  const start = 9 * 4;
  const end = 17 * 4;
  const day = new Array<boolean>(SLOTS_PER_DAY).fill(false);
  for (let i = start; i < end; i++) day[i] = true;
  return {
    mon: day.slice(),
    tue: day.slice(),
    wed: day.slice(),
    thu: day.slice(),
    fri: day.slice(),
    sat: grid.sat.slice(),
    sun: grid.sun.slice(),
  };
}

function clearAll(_grid: SlotGrid): SlotGrid {
  return {
    mon: emptyDay(),
    tue: emptyDay(),
    wed: emptyDay(),
    thu: emptyDay(),
    fri: emptyDay(),
    sat: emptyDay(),
    sun: emptyDay(),
  };
}
